import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { LogOut, Menu, X } from 'lucide-react';
import { can, type ClubSummary, type Me, type SessionState, type TeamSummary, type ThemePref } from '@hoopify/shared';
import { api, errorMessage } from '../lib/api';
import { useMe, useSetSession } from '../lib/session';
import { useAccountTheme } from '../lib/theme';
import { ThemeProvider, ThemeSwitch } from './ThemeSwitch';
import { initials } from '../lib/format';
import { NAV } from './nav';
import { Wordmark } from './Wordmark';
import { Button } from './ui';
import { NotificationBell } from './notifications/Notifications';

export function AppLayout() {
  const [more, setMore] = useState(false);
  const location = useLocation();
  useEffect(() => setMore(false), [location.pathname]);
  // The schedule and plan builders need the room.
  const wide = location.pathname === '/' || /^\/teams\/[^/]+\/sessions\/|^\/templates\/[^/]+$/.test(location.pathname);
  const me = useMe();
  const setSession = useSetSession();
  const theme = useAccountTheme(me.theme, async (t: ThemePref) => setSession({ status: 'signed_in', user: await api.put<Me>('/auth/theme', { theme: t }) }));

  return (
    <ThemeProvider value={theme}>
    <div className="min-h-dvh lg:grid lg:grid-cols-[240px_1fr]">
      {/* Phone/tablet top bar: just the wordmark */}
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-line bg-paper/95 px-4 backdrop-blur lg:hidden">
        <Link to="/" aria-label="Hoopify home">
          <Wordmark size={22} />
        </Link>
        <CoachBell />
      </div>

      {/* Laptop: side menu */}
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-line bg-surface lg:flex" aria-label="Main menu">
        <div className="flex h-20 items-center justify-between px-5">
          <Link to="/" aria-label="Hoopify home">
            <Wordmark size={24} />
          </Link>
          <CoachBell align="left" />
        </div>
        <Sidebar />
      </aside>

      <main className="min-w-0 pb-24 lg:pb-0">
        <VerifyBanner />
        <div className={`mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 ${wide ? 'max-w-[1240px]' : 'max-w-[960px]'}`}>
          <Outlet />
        </div>
      </main>

      <TabBar onMore={() => setMore(true)} moreOpen={more} />
      {more && <MoreSheet onClose={() => setMore(false)} />}
    </div>
    </ThemeProvider>
  );
}

function useIsCoach() {
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  // Someone with no teams yet is a new coach; someone only on teams as player/parent/read-only isn't.
  return !teams.data || !teams.data.length || teams.data.some((t) => can(t.role, 'sessions:write'));
}

/** The notifications bell, for head and assistant coaches. */
function CoachBell({ align }: { align?: 'left' | 'right' }) {
  const teams = useQuery({ queryKey: ['teams'], queryFn: () => api.get<TeamSummary[]>('/teams') });
  if (!teams.data?.some((t) => t.role === 'head_coach' || t.role === 'assistant_coach')) return null;
  return <NotificationBell align={align} />;
}

/** Shows the Club menu item to people who help run a club. */
function useHasClub() {
  const clubs = useQuery({ queryKey: ['clubs'], queryFn: () => api.get<ClubSummary[]>('/clubs'), staleTime: 60_000 });
  return !!clubs.data?.length;
}

function useLogout() {
  const setSession = useSetSession();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => api.post<SessionState>('/auth/logout'),
    onSuccess: (s) => {
      setSession(s);
      navigate('/login');
    },
  });
}

function Sidebar() {
  const me = useMe();
  const { pathname } = useLocation();
  const isCoach = useIsCoach();
  const hasClub = useHasClub();
  const logout = useLogout();

  return (
    <>
      <nav className="flex-1 space-y-1 px-3 py-2">
        {NAV.filter((item) => (!item.coachOnly || isCoach) && (!item.clubOnly || hasClub || isCoach)).map((item) => {
          const active = item.match ? item.match(pathname) : pathname === item.to;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={`relative flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-[15px] font-medium ${active ? 'bg-action-soft text-action' : 'text-muted hover:bg-sunken hover:text-ink'}`}
              aria-current={active ? 'page' : undefined}
            >
              {active && <span aria-hidden className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full bg-blue" />}
              <item.icon className="size-5" strokeWidth={2} aria-hidden />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="space-y-3 border-t border-line p-3">
        <ThemeSwitch />
        <div className="flex items-center gap-2.5 px-1">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-sunken text-[12px] font-bold text-muted" aria-hidden>
            {initials(me.name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[14px] font-medium">{me.name}</p>
            <p className="truncate text-[12px] text-muted">{me.email}</p>
          </div>
          <button className="btn-ghost p-2" onClick={() => logout.mutate()} aria-label="Sign out" title="Sign out">
            <LogOut className="size-4" />
          </button>
        </div>
      </div>
    </>
  );
}

/** Phones and tablets: 4–5 tabs; Help, Account and appearance live under More. */
const TAB_ITEMS = ['/', '/drills', '/teams', '/templates'];

function TabBar({ onMore, moreOpen }: { onMore: () => void; moreOpen: boolean }) {
  const { pathname } = useLocation();
  const isCoach = useIsCoach();
  const tabs = NAV.filter((n) => TAB_ITEMS.includes(n.to) && (!n.coachOnly || isCoach));
  const inMore = NAV.some((n) => !TAB_ITEMS.includes(n.to) && (n.match ? n.match(pathname) : pathname === n.to));
  const Tab = ({ active, icon: Icon, label }: { active: boolean; icon: typeof Menu; label: string }) => (
    <span className={`relative flex flex-col items-center gap-1 pt-2 pb-1.5 text-[12px] font-medium ${active ? 'text-action' : 'text-muted'}`}>
      <span aria-hidden className={`absolute top-0 h-[3px] w-6 rounded-full ${active ? 'bg-blue' : 'bg-transparent'}`} />
      <Icon className="size-6" strokeWidth={2} aria-hidden />
      {label}
    </span>
  );
  return (
    <nav aria-label="Main menu" className="fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden">
      <div className="mx-auto grid max-w-[600px]" style={{ gridTemplateColumns: `repeat(${tabs.length + 1}, minmax(0, 1fr))` }}>
        {tabs.map((t) => {
          const active = t.match ? t.match(pathname) : pathname === t.to;
          return (
            <NavLink key={t.to} to={t.to} aria-current={active ? 'page' : undefined} className="min-h-14">
              <Tab active={active} icon={t.icon} label={t.label} />
            </NavLink>
          );
        })}
        <button onClick={onMore} aria-expanded={moreOpen} aria-haspopup="dialog" className="min-h-14">
          <Tab active={inMore || moreOpen} icon={Menu} label="More" />
        </button>
      </div>
    </nav>
  );
}

function MoreSheet({ onClose }: { onClose: () => void }) {
  const me = useMe();
  const { pathname } = useLocation();
  const logout = useLogout();
  const hasClub = useHasClub();
  const isCoach = useIsCoach();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="More">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden />
      <div className="absolute inset-x-0 bottom-0 rounded-t-xl border-t border-line bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        <div className="mb-3 flex items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-sunken text-[13px] font-bold text-muted" aria-hidden>
            {initials(me.name)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium">{me.name}</p>
            <p className="truncate text-[13px] text-muted">{me.email}</p>
          </div>
          <button className="btn-ghost p-2" onClick={onClose} aria-label="Close">
            <X className="size-5" />
          </button>
        </div>
        <nav className="space-y-1">
          {NAV.filter((n) => !TAB_ITEMS.includes(n.to) && (!n.coachOnly || isCoach) && (!n.clubOnly || hasClub || isCoach)).map((n) => {
            const active = n.match ? n.match(pathname) : pathname === n.to;
            return (
              <NavLink key={n.to} to={n.to} onClick={onClose} className={`flex min-h-12 items-center gap-3 rounded-md px-3 text-[16px] font-medium ${active ? 'bg-action-soft text-action' : 'hover:bg-sunken'}`}>
                <n.icon className="size-5" aria-hidden /> {n.label}
              </NavLink>
            );
          })}
          <button onClick={() => logout.mutate()} className="flex min-h-12 w-full items-center gap-3 rounded-md px-3 text-left text-[16px] font-medium hover:bg-sunken">
            <LogOut className="size-5" aria-hidden /> Sign out
          </button>
        </nav>
        <div className="mt-4 border-t border-line pt-4">
          <p className="label-caps mb-2 text-muted">Appearance</p>
          <ThemeSwitch />
        </div>
      </div>
    </div>
  );
}

function VerifyBanner() {
  const me = useMe();
  const resend = useMutation({ mutationFn: () => api.post('/auth/verify-email/resend') });
  if (me.emailVerified) return null;
  return (
    <div className="border-b border-line bg-warn-soft px-4 py-2.5 text-[14px] sm:px-8">
      <div className="mx-auto flex max-w-[960px] flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <p>
          Confirm your email address to create teams. We sent a link to <strong className="font-semibold">{me.email}</strong>.
        </p>
        {resend.isSuccess ? (
          <span className="text-ok">Sent. Check your inbox.</span>
        ) : (
          <Button variant="ghost" loading={resend.isPending} onClick={() => resend.mutate()} className="btn-ghost -my-1 text-ink underline-offset-2 hover:underline">
            Send it again
          </Button>
        )}
        {resend.isError && <span className="w-full text-danger">{errorMessage(resend.error)}</span>}
      </div>
    </div>
  );
}
